import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withNamespaces } from 'react-i18next';
import { withApollo } from 'react-apollo';
import classnames from 'classnames';

import combineStyles from '../../utils/combineStyles';
import TypographyStyles from '../../styles/typography';
import UploadedDocumentStyles from './__styles__/UploadedDocument.styles';

import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import InsertPhotoIcon from '@material-ui/icons/InsertPhotoOutlined';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFileOutlined';

import ConfirmDialogModal from './Modals/ConfirmDialog';

import {
  deleteFile as deleteFileMutation,
  updateFile as updateFileMutation
} from '../../graphQL/mutations/shipmentMutations';

import { getFileUrl } from '../../utils/PropelFileUploader';

import TagShipmentDocumentType from './Modals/TagShipmentDocumentType.js';

import { sanitize } from '../../Models/File';

import { AppContext } from '../../AppProvider';

const DOCUMENT = 'PDF';
const SYSTEM = 'SYSTEM';

export class UploadedDocument extends Component {
  static propTypes = {
    file: PropTypes.object.isRequired,
    editable: PropTypes.bool.isRequired,
    printFile: PropTypes.func,
    sendFile: PropTypes.func
  };

  state = {
    optionsMenu: null,
    openConfirmDeleteNotificationModal: false,
    openTagFormModal: false,
    isSubmitting: false
  };

  _handleOpenOptionsMenu = event => {
    this.setState({ optionsMenu: event.currentTarget });
  };

  _handleCloseOptionsMenu = () => {
    this.setState({ optionsMenu: null });
  };

  _handleViewDocument = async () => {
    const {
      file: { originalFileDownloadUrlSas, fileTypeId }
    } = this.props;

    const fileURL = await getFileUrl(originalFileDownloadUrlSas, fileTypeId);
    window.open(fileURL);

    this._handleCloseOptionsMenu();
  };

  _handleOpenConfirmDeleteNotificationModal = () => {
    this.setState(
      { openConfirmDeleteNotificationModal: true },
      this._handleCloseOptionsMenu
    );
  };

  _handleCloseConfirmDeleteNotificationModal = () => {
    this.setState({ openConfirmDeleteNotificationModal: false });
  };

  _handleOpenTagFormModal = () => {
    this.setState({ openTagFormModal: true }, this._handleCloseOptionsMenu);
  };

  _handleCloseTagFormModal = () => {
    this.setState({ openTagFormModal: false });
  };

  _handleManageTag = async tag => {
    this.setState({ isSubmitting: true });
    const { client: ApolloClient, file, reloadFiles } = this.props;

    try {
      await ApolloClient.mutate({
        mutation: updateFileMutation,
        variables: {
          file: {
            ...sanitize(file),
            ...tag
          }
        }
      });
      this.setState({ isSubmitting: false });
      await reloadFiles();
    } catch (error) {
      console.error(error);
    }
  };

  _handleDelete = async () => {
    this.setState({ deleting: true });
    const {
      client: ApolloClient,
      file: { id },
      reloadFiles
    } = this.props;
    try {
      await ApolloClient.mutate({
        mutation: deleteFileMutation,
        variables: { shipmentFileId: parseInt(id) }
      });
      await reloadFiles();
    } catch (error) {
      console.error(error);
    }
  };

  _getTag = () => {
    // override logic for system files (BOL & LABELS).  These are a SYSTEM file
    // type which won't be in the ref data enum.
    const { file } = this.props;
    if (file.fileTypeId === 'SYSTEM') {
      return file.tag;
    }

    const { globalProps = {} } = this.context;

    const { referenceData = {} } = globalProps;

    const {
      file: { shipmentDocumentTypeId }
    } = this.props;

    const shipmentDocumentType =
      referenceData.shipmentDocumentTypes &&
      referenceData.shipmentDocumentTypes.find(
        shipmentDocumentType =>
          shipmentDocumentType.value === shipmentDocumentTypeId
      );

    if (shipmentDocumentType) {
      return shipmentDocumentType.title;
    }

    return null;
  };

  _printFile = () => {
    this._handleCloseOptionsMenu();
    this.props.printFile();
  };

  _sendFile = () => {
    this._handleCloseOptionsMenu();
    this.props.sendFile();
  };

  render() {
    const {
      t,
      classes,
      file: { uploadedFileName, fileTypeId, shipmentDocumentTypeId },
      editable
    } = this.props;

    return (
      <div className={classes.root}>
        <div className={classes.fileTypeContainer}>
          {fileTypeId === DOCUMENT && (
            <InsertDriveFileIcon className={classes.fileTypeIcon} />
          )}
          {fileTypeId === SYSTEM && (
            <InsertDriveFileIcon className={classes.fileTypeIcon} />
          )}
          {fileTypeId !== DOCUMENT && fileTypeId !== SYSTEM && (
            <InsertPhotoIcon className={classes.fileTypeIcon} />
          )}
        </div>
        <div className={classes.detail}>
          <p className={classnames(classes.subtitle3, classes.name)}>
            {this._getTag() || uploadedFileName}
          </p>
          <p className={classnames(classes.body2, classes.fileName)}>
            {uploadedFileName}
          </p>
        </div>
        <div className={classnames(classes.actions, 'menu')}>
          <IconButton
            aria-label="options"
            aria-owns={this.state.optionsMenu ? 'long-menu' : undefined}
            aria-haspopup="true"
            onClick={this._handleOpenOptionsMenu}
          >
            <MoreVertIcon className={classes.optionsIcon} />
          </IconButton>
          {fileTypeId !== SYSTEM && (
            <Menu
              id="long-menu"
              anchorEl={this.state.optionsMenu}
              open={Boolean(this.state.optionsMenu)}
              onClose={this._handleCloseOptionsMenu}
              classes={{
                paper: classes.menu
              }}
            >
              <MenuItem
                onClick={this._handleViewDocument}
                classes={{
                  root: classes.menuItemRoot,
                  selected: classes.menuItemSelected
                }}
              >
                {t('View')}
              </MenuItem>
              {editable && (
                <MenuItem
                  onClick={this._handleOpenTagFormModal}
                  classes={{
                    root: classes.menuItemRoot,
                    selected: classes.menuItemSelected
                  }}
                >
                  {shipmentDocumentTypeId ? t('Edit Tag') : t('Add Tag')}
                </MenuItem>
              )}
              {editable && (
                <MenuItem
                  onClick={this._handleOpenConfirmDeleteNotificationModal}
                  classes={{
                    root: classes.menuItemRoot,
                    selected: classes.menuItemSelected
                  }}
                >
                  {t('Delete')}
                </MenuItem>
              )}
            </Menu>
          )}

          {fileTypeId === SYSTEM && (
            <Menu
              id="long-menu"
              anchorEl={this.state.optionsMenu}
              open={Boolean(this.state.optionsMenu)}
              onClose={this._handleCloseOptionsMenu}
              classes={{
                paper: classes.menu
              }}
            >
              <MenuItem
                onClick={this._sendFile}
                classes={{
                  root: classes.menuItemRoot,
                  selected: classes.menuItemSelected
                }}
              >
                {t('Send')}
              </MenuItem>
              <MenuItem
                onClick={this._printFile}
                classes={{
                  root: classes.menuItemRoot,
                  selected: classes.menuItemSelected
                }}
              >
                {t('Print')}
              </MenuItem>
            </Menu>
          )}
        </div>
        <ConfirmDialogModal
          open={this.state.openConfirmDeleteNotificationModal}
          title={t('Are you sure you want to delete this document?')}
          message={uploadedFileName}
          confirmLabel={t('Delete')}
          onClose={this._handleCloseConfirmDeleteNotificationModal}
          onConfirm={this._handleDelete}
          isSubmitting={this.state.deleting}
        />
        {this.state.openTagFormModal && (
          <TagShipmentDocumentType
            shipmentDocumentTypeId={shipmentDocumentTypeId}
            onSave={this._handleManageTag}
            onClose={this._handleCloseTagFormModal}
            isSubmitting={this.state.isSubmitting}
          />
        )}
      </div>
    );
  }
}

UploadedDocument.contextType = AppContext;

export default withStyles(
  combineStyles(UploadedDocumentStyles, TypographyStyles)
)(withNamespaces()(withApollo(UploadedDocument)));
